import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Trash2 } from 'lucide-react';
import api from '../../../connector/api';
import GradingSchemaModal from '../modal/GradingSchemaModal';
import DeleteConfirmModal from '../modal/DeteleConfirmModal';

export default function GradingSchemaTab() {
    const [schemas, setSchemas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSchema, setSelectedSchema] = useState(null);

    // State for Delete Confirmation Modal
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [schemaToDelete, setSchemaToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const fetchSchemas = async () => {
        try {
            setLoading(true);
            const res = await api.get('grading-schemas/');
            setSchemas(res.data.results || res.data || []);
        } catch (err) {
            console.error("Failed to fetch grading schemas:", err);
            toast.error("Error loading grading schemas.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSchemas();
    }, []);

    const handleOpenCreateModal = () => {
        setSelectedSchema(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (schema) => {
        setSelectedSchema(schema);
        setIsModalOpen(true);
    };

    const handleOpenDeleteModal = (schema) => {
        setSchemaToDelete(schema);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!schemaToDelete) return;
        setIsDeleting(true);
        try {
            await api.delete(`grading-schemas/${schemaToDelete.id}/`);
            toast.success("Grading schema deleted successfully!");
            fetchSchemas();
            setIsDeleteModalOpen(false);
        } catch (err) {
            console.error("Failed to delete schema:", err);
            toast.error("Failed to delete schema.");
        } finally {
            setIsDeleting(false);
            setSchemaToDelete(null);
        }
    };

    const handleSetDefault = async (schema) => {
        try {
            await api.patch(`grading-schemas/${schema.id}/`, { is_default: true });
            toast.success(`${schema.name} is now set as default.`);
            fetchSchemas();
        } catch (err) {
            console.error("Failed to set default schema:", err);
            toast.error("Failed to update default status.");
        }
    };

    return (
        <div className="space-y-6">
            {/* Header & Primary Action */}
            <div className="bg-white border border-zinc-200 font-dynamic-title rounded-xl p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-900 tracking-tight">
                        Grading Schemas & Grade Brackets
                    </h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Define global letter grade scales, minimum score thresholds, and UI display colors.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleOpenCreateModal}
                    className="px-3.5 py-2 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-xs"
                >
                    + Add New Schema
                </button>
            </div>

            {/* Schema List / Cards */}
            {loading ? (
                <div className="p-8 text-center text-xs text-zinc-500 bg-white border border-zinc-200 rounded-xl">
                    Loading grading schemas...
                </div>
            ) : schemas.length === 0 ? (
                <div className="p-8 text-center text-xs text-zinc-500 bg-white border border-zinc-200 rounded-xl">
                    No grading schemas configured yet. Click above to create one.
                </div>
            ) : (
                <div className="space-y-4">
                    {schemas.map((schema) => (
                        <div
                            key={schema.id}
                            className="bg-white border border-zinc-200 rounded-xl shadow-xs p-5 space-y-4"
                        >
                            {/* Card Header */}
                            <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                                <div className="flex items-center gap-2.5">
                                    <h3 className="text-sm font-bold text-zinc-900">{schema.name}</h3>
                                    {schema.is_default && (
                                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                                            Default Scale
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    {!schema.is_default && (
                                        <button
                                            onClick={() => handleSetDefault(schema)}
                                            className="px-2.5 py-1 text-xs font-medium text-zinc-600 hover:text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-md hover:bg-zinc-100 transition-colors mr-1"
                                        >
                                            Make Default
                                        </button>
                                    )}
                                    
                                    {/* Edit Icon Button */}
                                    <button
                                        type="button"
                                        title="Edit Schema"
                                        onClick={() => handleOpenEditModal(schema)}
                                        className="p-1.5 text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-100 rounded-md transition-colors"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>

                                    {/* Delete Icon Button */}
                                    <button
                                        type="button"
                                        title="Delete Schema"
                                        onClick={() => handleOpenDeleteModal(schema)}
                                        className="p-1.5 text-rose-600 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-100 rounded-md transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>

                            {/* Brackets Display Table */}
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs text-zinc-700">
                                    <thead className="bg-zinc-50 border-y border-zinc-200 text-zinc-500 uppercase tracking-wider font-semibold text-[10px]">
                                        <tr>
                                            <th className="py-2 px-3">Grade</th>
                                            <th className="py-2 px-3">Score Range</th>
                                            <th className="py-2 px-3">Display Color</th>
                                            <th className="py-2 px-3">Description</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-100">
                                        {schema.brackets && schema.brackets.length > 0 ? (
                                            schema.brackets.map((bracket, idx) => (
                                                <tr key={idx} className="hover:bg-zinc-50/50">
                                                    <td className="py-2.5 px-3 font-bold text-zinc-900">
                                                        {bracket.letter_grade}
                                                    </td>
                                                    <td className="py-2.5 px-3">
                                                        {bracket.min_score}% – {bracket.max_score}%
                                                    </td>
                                                    <td className="py-2.5 px-3">
                                                        <div className="flex items-center gap-2">
                                                            <span
                                                                className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                                                                style={{ backgroundColor: bracket.color_code || '#3B82F6' }}
                                                            />
                                                            <span className="font-mono text-[11px] text-zinc-500">
                                                                {bracket.color_code}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-zinc-500">
                                                        {bracket.description || "—"}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="4" className="py-3 px-3 text-center text-zinc-400 italic">
                                                    No grade brackets configured for this scale.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <GradingSchemaModal
                    schema={selectedSchema}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={() => {
                        setIsModalOpen(false);
                        fetchSchemas();
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Grading Schema"
                message={`Are you sure you want to delete "${schemaToDelete?.name}"? This action cannot be undone.`}
                isDeleting={isDeleting}
            />
        </div>
    );
}